import { type AppEnv } from '@/platform/config/env'

import { isMockLearningEnabled } from './learning'
import { type LoadSampleMaterials } from './screens/materials/model'
import { type LoadInsightPreview } from './screens/parent/insight'

/**
 * Chooses whether DEMO content may be shown: the Learning Insight preview
 * (Slice 1 product shell) and the sample school materials (Slice 2). Same
 * rule as the MOCK learning service (decision D-02): only local/development,
 * never pilot, guarded twice:
 *
 *   - build time: `import.meta.env.VITE_APP_ENV` is inlined by Vite, so the
 *     dynamic import of the demo data is removed from other builds (the check
 *     stays inline in each loader so the bundler can see it);
 *   - run time: the validated environment, which keeps the decision testable.
 *
 * Returns null when no demo may be shown; screens then show only real states.
 */
export function createDemoInsightLoader(env: AppEnv): LoadInsightPreview | null {
  if (!isMockLearningEnabled(env.VITE_APP_ENV)) return null
  const buildAllowsDemo =
    import.meta.env.VITE_APP_ENV === 'local' ||
    import.meta.env.VITE_APP_ENV === 'development'
  if (!buildAllowsDemo) return null
  return async () => (await import('./screens/parent/demo-insight')).DEMO_INSIGHT_PREVIEW
}

export function createDemoMaterialsLoader(env: AppEnv): LoadSampleMaterials | null {
  if (!isMockLearningEnabled(env.VITE_APP_ENV)) return null
  const buildAllowsDemo =
    import.meta.env.VITE_APP_ENV === 'local' ||
    import.meta.env.VITE_APP_ENV === 'development'
  if (!buildAllowsDemo) return null
  return async () =>
    (await import('./screens/materials/demo-materials')).DEMO_SAMPLE_MATERIALS
}
